import { Dispatch } from 'react-redux';

import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import { IndesignTextFieldsConfig } from './utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from './utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from './utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from './utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from './utils/editableLinks/getData';
import {
	PageData,
	ResourcesState,
	EditableData,
	ZoomType,
	ConfigsData,
	BackgroundMediaData,
} from './reducer';
import { createNotification } from '../NotificationBar/actions';
import { updateDriveItem, updateDriveItemName } from '../DriveItemList/actions';
import * as selectors from './selectors';
import ApiUtils from '../../utils/api';
import { Api } from '../../typings/api';
import htmlEditorUtils from './utils/htmlEditor';
import indesignEditorUtils from './utils/indesignEditor';
import RoutesUtils from '../../utils/routes';
import store from '../../store';
import { RoutePath } from '../../routes/constants';
import { EditorType } from './constants';
import { getPageHtmlPath } from './utils/helpers';

export enum EditorActionKeys {
	GET_DATA = '@@editor/GET_DATA',
	SET_DATA = '@@editor/SET_DATA',
	SET_EDITOR_TYPE = '@@editor/SET_EDITOR_TYPE',
	SET_PAGE_DATA = '@@editor/SET_PAGE_DATA',
	SET_RESOURCES_DATA = '@@editor/SET_RESOURCES_DATA',
	SET_CONFIGS_DATA = '@@editor/SET_CONFIGS_DATA',
	SET_EDITABLE_DATA = '@@editor/SET_EDITABLE_DATA',
	SET_EDITABLE_TEXT_FIELDS_DATA = '@@editor/SET_EDITABLE_TEXT_FIELDS_DATA',
	UPDATE_EDITABLE_TEXT_FIELD = '@@editor/UPDATE_EDITABLE_TEXT_FIELD',
	SET_EDITABLE_MEDIA_DATA = '@@editor/SET_EDITABLE_MEDIA_DATA',
	UPDATE_EDITABLE_MEDIA = '@@editor/UPDATE_EDITABLE_MEDIA',
	SET_EDITABLE_LINKS_DATA = '@@editor/SET_EDITABLE_LINKS_DATA',
	UPDATE_EDITABLE_LINK = '@@editor/UPDATE_EDITABLE_LINK',
	SET_IS_EDITOR_LOADED = '@@editor/SET_IS_EDITOR_LOADED',
	ADD_PAGE = '@@editor/ADD_PAGE',
	CHANGE_PAGE = '@@editor/CHANGE_PAGE',
	SAVE_PAGE = '@@editor/SAVE_PAGE',
	SET_THUMBNAILS = '@@editor/SET_THUMBNAILS',
	TOGGLE_THUMBNAILS = '@@editor/TOGGLE_THUMBNAILS',
	TOGGLE_EDIT_PANEL = '@@editor/TOGGLE_EDIT_PANEL',
	MOVE_EDIT_PANEL = '@@editor/MOVE_EDIT_PANEL',
	SET_ACTIVE_ITEM = '@@editor/SET_ACTIVE_ITEM',
	RESET_ACTIVE_ITEM = '@@editor/RESET_ACTIVE_ITEM',
	CREATE_COMBO = '@@editor/CREATE_COMBO',
	UPDATE_COMBO_PERCENTAGE = '@@editor/UPDATE_COMBO_PERCENTAGE',
	FINISH_COMBO = '@@editor/FINISH_COMBO',
	SET_IS_RENAMING = '@@editor/SET_IS_RENAMING',
	RENAME = '@@editor/RENAME',
	SET_RENAME_ERROR = '@@editor/SET_RENAME_ERROR',
	UPDATE_VERSION = '@@editor/UPDATE_VERSION',
	SET_PAGE_SELECTION = '@@editor/SET_PAGE_SELECTION',
	RESET_PAGE_SELECTION = '@@editor/RESET_PAGE_SELECTION',
	SELECT_PAGE = '@@editor/SELECT_PAGE',
	RESTORE_PAGE_SELECTION = '@@editor/RESTORE_PAGE_SELECTION',
	ZOOM_PAGE = '@@editor/ZOOM_PAGE',
	SET_IS_PAGE_BACKGROUND_ACTIVE = '@@editor/SET_IS_PAGE_BACKGROUND_ACTIVE',
	SET_PAGE_BACKGROUND = '@@editor/SET_PAGE_BACKGROUND',
	RESET = '@@editor/RESET',
}

interface EditableTextFieldData {
	content: string;
}

export interface EditableMediaData {
	frameWidth?: number;
	frameHeight?: number;
	frameAspectRatio?: number;
	width?: number;
	height?: number;
	x?: number;
	y?: number;
	driveLink?: string;
	externalLink?: string;
	label?: string;
	isExternalLinkActive?: boolean;
	canDragAndResize?: boolean;
	isLoaded?: boolean;
	isEditedFromBackoffice?: boolean;
}

interface EditableLinkData {
	href: string;
}

interface GetDataAction extends Action {
	type: EditorActionKeys.GET_DATA;
	clientId: number;
	resourceId: number;
}

interface SetDataAction extends Action {
	type: EditorActionKeys.SET_DATA;
	data: Api.IndesignEditorGeneralData | Api.HtmlEditorData;
}

interface SetEditorTypeAction extends Action {
	type: EditorActionKeys.SET_EDITOR_TYPE;
	editorType: EditorType;
}

interface SetPageDataAction extends Action {
	type: EditorActionKeys.SET_PAGE_DATA;
	data: PageData;
}

interface SetResourcesDataAction extends Action {
	type: EditorActionKeys.SET_RESOURCES_DATA;
	data: ResourcesState;
}

interface SetConfigsDataAction extends Action {
	type: EditorActionKeys.SET_CONFIGS_DATA;
	data: ConfigsData;
}

interface SetEditableDataAction extends Action {
	type: EditorActionKeys.SET_EDITABLE_DATA;
	page: number;
	data: EditableData;
}

interface SetEditableTextFieldsDataAction extends Action {
	type: EditorActionKeys.SET_EDITABLE_TEXT_FIELDS_DATA;
	page: number;
	data: IndesignTextFieldsConfig[] | HtmlTextFieldsConfig[];
}

interface UpdateEditableTextFieldAction extends Action {
	type: EditorActionKeys.UPDATE_EDITABLE_TEXT_FIELD;
	page: number;
	id: string;
	data: EditableTextFieldData;
}

interface SetEditableMediaDataAction extends Action {
	type: EditorActionKeys.SET_EDITABLE_MEDIA_DATA;
	page: number;
	data: IndesignMediaElementConfig[] | HtmlMediaElementConfig[];
}

interface UpdateEditableMediaAction extends Action {
	type: EditorActionKeys.UPDATE_EDITABLE_MEDIA;
	page: number;
	id: string;
	data: EditableMediaData;
}

interface SetEditableLinksDataAction extends Action {
	type: EditorActionKeys.SET_EDITABLE_LINKS_DATA;
	page: number;
	data: LinkConfig[];
}

interface UpdateEditableLinkAction extends Action {
	type: EditorActionKeys.UPDATE_EDITABLE_LINK;
	page: number;
	id: string;
	data: EditableLinkData;
}

interface SetIsEditorLoadedAction extends Action {
	type: EditorActionKeys.SET_IS_EDITOR_LOADED;
	isLoaded: boolean;
}

interface AddPageAction extends Action {
	type: EditorActionKeys.ADD_PAGE;
	page: number;
	width?: number;
	height?: number;
}

interface ChangePageAction extends Action {
	type: EditorActionKeys.CHANGE_PAGE;
	page: number;
}

interface SavePageAction extends Action {
	type: EditorActionKeys.SAVE_PAGE;
	page: number;
}

interface SetThumbnailsAction extends Action {
	type: EditorActionKeys.SET_THUMBNAILS;
	data: Api.EditorThumbnailData[];
}

interface ToggleThumbnailsAction extends Action {
	type: EditorActionKeys.TOGGLE_THUMBNAILS;
}

interface ToggleEditPanelAction extends Action {
	type: EditorActionKeys.TOGGLE_EDIT_PANEL;
}

interface MoveEditPanelAction extends Action {
	type: EditorActionKeys.MOVE_EDIT_PANEL;
	x: number;
	y: number;
}

interface SetActiveItemAction extends Action {
	type: EditorActionKeys.SET_ACTIVE_ITEM;
	id: string;
	parentId: string | null;
}

interface ResetActiveItemAction extends Action {
	type: EditorActionKeys.RESET_ACTIVE_ITEM;
}

interface FinishComboAction extends Action {
	type: EditorActionKeys.FINISH_COMBO;
}

interface CreateComboAction extends Action {
	type: EditorActionKeys.CREATE_COMBO;
}

interface UpdateComboPercentageAction extends Action {
	type: EditorActionKeys.UPDATE_COMBO_PERCENTAGE;
	percentage: number;
}

interface SetIsRenamingAction extends Action {
	type: EditorActionKeys.SET_IS_RENAMING;
	isRenaming: boolean;
}

interface RenameAction extends Action {
	type: EditorActionKeys.RENAME;
	newLabel: string;
}

interface SetRenameErrorAction extends Action {
	type: EditorActionKeys.SET_RENAME_ERROR;
	isError: boolean;
}

interface UpdateVersionAction extends Action {
	type: EditorActionKeys.UPDATE_VERSION;
}

interface SetPageSelectionAction extends Action {
	type: EditorActionKeys.SET_PAGE_SELECTION;
	currentPage: number;
}

interface ResetPageSelectionAction extends Action {
	type: EditorActionKeys.RESET_PAGE_SELECTION;
}

interface SelectPageAction extends Action {
	type: EditorActionKeys.SELECT_PAGE;
	page: number;
	totalPages: number;
	isCtrlPressed?: boolean;
	isShiftPressed?: boolean;
}

interface RestorePageSelectionAction extends Action {
	type: EditorActionKeys.RESTORE_PAGE_SELECTION;
	pages: number[];
}

interface ZoomPageAction extends Action {
	type: EditorActionKeys.ZOOM_PAGE;
	zoom: ZoomType;
}

interface SetIsPageBackgroundActiveAction extends Action {
	type: EditorActionKeys.SET_IS_PAGE_BACKGROUND_ACTIVE;
	isActive: boolean;
}

interface SetPageBackgroundAction extends Action {
	type: EditorActionKeys.SET_PAGE_BACKGROUND;
	page: number;
	data: BackgroundMediaData | null;
	isGeneralBgData: boolean;
}

interface ResetAction extends Action {
	type: EditorActionKeys.RESET;
}

export type EditorAction =
	GetDataAction |
	SetDataAction |
	SetEditorTypeAction |
	SetPageDataAction |
	SetResourcesDataAction |
	SetConfigsDataAction |
	SetEditableDataAction |
	SetEditableTextFieldsDataAction |
	UpdateEditableTextFieldAction |
	SetEditableMediaDataAction |
	UpdateEditableMediaAction |
	SetEditableLinksDataAction |
	UpdateEditableLinkAction |
	SetIsEditorLoadedAction |
	AddPageAction |
	ChangePageAction |
	SavePageAction |
	SetThumbnailsAction |
	ToggleThumbnailsAction |
	ToggleEditPanelAction |
	MoveEditPanelAction |
	SetActiveItemAction |
	ResetActiveItemAction |
	FinishComboAction |
	CreateComboAction |
	UpdateComboPercentageAction |
	SetIsRenamingAction |
	RenameAction |
	SetRenameErrorAction |
	UpdateVersionAction |
	SetPageSelectionAction |
	ResetPageSelectionAction |
	SelectPageAction |
	RestorePageSelectionAction |
	ZoomPageAction |
	SetIsPageBackgroundActiveAction |
	SetPageBackgroundAction |
	ResetAction;

export const setData = (data: Api.IndesignEditorGeneralData | Api.HtmlEditorData): SetDataAction => ({
	type: EditorActionKeys.SET_DATA,
	data,
});

export const setEditorType = (editorType: EditorType): SetEditorTypeAction => ({
	type: EditorActionKeys.SET_EDITOR_TYPE,
	editorType,
});

export const setPageData = (data: PageData): SetPageDataAction => ({
	type: EditorActionKeys.SET_PAGE_DATA,
	data,
});

export const setResourcesData = (data: ResourcesState): SetResourcesDataAction => ({
	type: EditorActionKeys.SET_RESOURCES_DATA,
	data,
});

export const setConfigsData = (data: ConfigsData): SetConfigsDataAction => ({
	type: EditorActionKeys.SET_CONFIGS_DATA,
	data,
});

export const setEditableData = (page: number, data: EditableData): SetEditableDataAction => ({
	type: EditorActionKeys.SET_EDITABLE_DATA,
	page,
	data,
});

export const setEditableTextFieldsData = (page: number, data: IndesignTextFieldsConfig[] | HtmlTextFieldsConfig[]): SetEditableTextFieldsDataAction => ({
	type: EditorActionKeys.SET_EDITABLE_TEXT_FIELDS_DATA,
	page,
	data,
});

export const setEditableMediaData = (page: number, data: IndesignMediaElementConfig[] | HtmlMediaElementConfig[]): SetEditableMediaDataAction => ({
	type: EditorActionKeys.SET_EDITABLE_MEDIA_DATA,
	page,
	data,
});

export const setEditableLinksData = (page: number, data: LinkConfig[]): SetEditableLinksDataAction => ({
	type: EditorActionKeys.SET_EDITABLE_LINKS_DATA,
	page,
	data,
});

export const setIsEditorLoaded = (isLoaded: boolean): SetIsEditorLoadedAction => ({
	type: EditorActionKeys.SET_IS_EDITOR_LOADED,
	isLoaded,
});

export const addPage = (page: number, width?: number, height?: number): AddPageAction => ({
	type: EditorActionKeys.ADD_PAGE,
	page,
	width,
	height,
});

export const setThumbnails = (data: Api.EditorThumbnailData[]): SetThumbnailsAction => ({
	type: EditorActionKeys.SET_THUMBNAILS,
	data,
});

export const toggleThumbnails = (): ToggleThumbnailsAction => ({
	type: EditorActionKeys.TOGGLE_THUMBNAILS,
});

export const toggleEditPanel = (): ToggleEditPanelAction => ({
	type: EditorActionKeys.TOGGLE_EDIT_PANEL,
});

export const moveEditPanel = (x: number, y: number): MoveEditPanelAction => ({
	type: EditorActionKeys.MOVE_EDIT_PANEL,
	x,
	y,
});

export const setActiveItem = (id: string, parentId: string | null): SetActiveItemAction => ({
	type: EditorActionKeys.SET_ACTIVE_ITEM,
	id,
	parentId,
});

export const resetActiveItem = (): ResetActiveItemAction => ({
	type: EditorActionKeys.RESET_ACTIVE_ITEM,
});

export const finishCombo = (): FinishComboAction => ({
	type: EditorActionKeys.FINISH_COMBO,
});

export const setIsRenaming = (isRenaming: boolean): SetIsRenamingAction => ({
	type: EditorActionKeys.SET_IS_RENAMING,
	isRenaming,
});

export const rename = (newLabel: string): RenameAction => ({
	type: EditorActionKeys.RENAME,
	newLabel,
});

export const setRenameError = (isError: boolean): SetRenameErrorAction => ({
	type: EditorActionKeys.SET_RENAME_ERROR,
	isError,
});

export const updateVersion = (): UpdateVersionAction => ({
	type: EditorActionKeys.UPDATE_VERSION,
});

export const setPageSelection = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			slider: { page },
			pageSelection: { shouldDisplayNotification },
		}
	} = getState();

	const action: SetPageSelectionAction = {
		type: EditorActionKeys.SET_PAGE_SELECTION,
		currentPage: page,
	};

	dispatch(action);

	if (shouldDisplayNotification) {
		dispatch(createNotification('success.editor_multiple_page_selection'));
	}
};

export const resetPageSelection = (): ResetPageSelectionAction => ({
	type: EditorActionKeys.RESET_PAGE_SELECTION,
});

export const selectPage = (page: number, isCtrlPressed?: boolean, isShiftPressed?: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			slider: { totalPages },
		}
	} = getState();

	dispatch({
		type: EditorActionKeys.SELECT_PAGE,
		page,
		totalPages,
		isCtrlPressed,
		isShiftPressed,
	});
};

export const restorePageSelection = (pages: number[]): RestorePageSelectionAction => ({
	type: EditorActionKeys.RESTORE_PAGE_SELECTION,
	pages,
});

export const zoomPage = (zoom: ZoomType): ZoomPageAction => ({
	type: EditorActionKeys.ZOOM_PAGE,
	zoom,
});

export const setIsPageBackgroundActive = (isActive: boolean): SetIsPageBackgroundActiveAction => ({
	type: EditorActionKeys.SET_IS_PAGE_BACKGROUND_ACTIVE,
	isActive,
});

export const reset = (): ResetAction => ({
	type: EditorActionKeys.RESET,
});

export const updateEditableTextField = (page: number, id: string, data: EditableTextFieldData) => (dispatch: Dispatch<StoreState>) => {
	const action: UpdateEditableTextFieldAction = {
		type: EditorActionKeys.UPDATE_EDITABLE_TEXT_FIELD,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(savePage(page));
};

export const updateEditableMedia = (
	page: number,
	id: string,
	data: EditableMediaData,
	shouldSave: boolean = true,
) => (dispatch: Dispatch<StoreState>) => {
	const action: UpdateEditableMediaAction = {
		type: EditorActionKeys.UPDATE_EDITABLE_MEDIA,
		page,
		id,
		data,
	};
	dispatch(action);

	if (shouldSave) {
		dispatch(savePage(page));
	}
};

export const updateEditableLink = (page: number, id: string, data: EditableLinkData) => (dispatch: Dispatch<StoreState>) => {
	const action: UpdateEditableLinkAction = {
		type: EditorActionKeys.UPDATE_EDITABLE_LINK,
		page,
		id,
		data,
	};
	dispatch(action);
	dispatch(savePage(page));
};

export const getResourcesData = (
	resourceId: number,
	cssPath?: string,
	configsPath?: string,
	jsPath?: string,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const promises: Promise<string>[] = [];

		if (cssPath) {
			promises.push(ApiUtils.getEditorResource(resourceId, cssPath));
		}

		if (configsPath) {
			promises.push(ApiUtils.getEditorResource(resourceId, configsPath));
		}

		if (jsPath) {
			promises.push(ApiUtils.getEditorResource(resourceId, jsPath));
		}

		const [css, configs, scripts] = await Promise.all(promises);

		const { galleryInfos: { config } } = getState();

		if (!isEditorOpened()) {
			return;
		}

		dispatch(setResourcesData({
			css: indesignEditorUtils.getModifiedCss(config, css, resourceId),
			scripts: scripts && indesignEditorUtils.getModifiedScripts(scripts),
		}));

		if (configs) {
			const parsedConfigs = indesignEditorUtils.getParsedConfigs(configs);
			dispatch(setConfigsData(parsedConfigs));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const parseHtmlEditorPage = (
	page: number,
	pageHtml: string,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {

	const isEditable = selectors.isEditablePage(getState());
	const {
		html,
		defaultHtml,
		fontsPath,
		googleFontsPath,
		editableTextFields,
		editableMedia,
		editableLinks,
		isError,
	} = htmlEditorUtils.getModifiedPageData(page, pageHtml, isEditable);

	if (isError) {
		return dispatch(createNotification('error.parse_editor_data_error'));
	}

	if (isEditable && !htmlEditorUtils.isModifiedFromBackoffice(pageHtml)) {
		try {
			// we need to save html with set ids even user hasn't modified anything
			// otherwise next time user goes to this page, new ids will be set and
			// it will break editor
			const { clientId, resourceId } = getClientIdAndResourceId();
			ApiUtils.saveHtmlEditorPage(clientId, resourceId, defaultHtml);
		} catch (error) {
			dispatch(createNotification('error.parse_editor_data_error'));
		}
	}

	dispatch(setPageData({
		page,
		defaultHtml,
		html,
		fontsPath,
		googleFontsPath,
	}));

	if (isEditable) {
		if (editableTextFields) {
			const { items, editableData } = editableTextFields;
			dispatch(setEditableTextFieldsData(page, items));
			dispatch(setEditableData(page, {
				textFieldsData: editableData,
			}));
		}

		if (editableMedia) {
			dispatch(setEditableMediaData(page, editableMedia));
		}

		if (editableLinks) {
			dispatch(setEditableLinksData(page, editableLinks));
		}
	}
};

const parseIndesignEditorPage = (
	page: number,
	pageHtml: string,
	resourceId: number,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			resources: { css },
			configs: {
				generalData: {
					editablePublication: {
						[page]: { modifiedFromBackoffice = false } = {},
					},
				},
				pagesData,
			},
		},
		galleryInfos: { config },
	} = getState();

	const { height: pageHeightFromConfig } = pagesData[page - 1];
	const isEditable = selectors.isEditablePage(getState());
	const {
		html,
		width,
		height,
		editableTextFields,
		editableMedia,
		editableLinks,
		isError,
	} = indesignEditorUtils.getModifiedPageData(
		page,
		pageHtml,
		css,
		config,
		resourceId,
		isEditable,
		modifiedFromBackoffice,
		pageHeightFromConfig,
	);

	if (isError) {
		return dispatch(createNotification('error.parse_editor_data_error'));
	}

	dispatch(setPageData({
		page,
		defaultHtml: pageHtml,
		html,
		width,
		height,
	}));

	if (isEditable) {
		if (editableTextFields) {
			const { items, editableData } = editableTextFields;
			dispatch(setEditableTextFieldsData(page, items));

			if (!modifiedFromBackoffice) {
				dispatch(saveEditableData(page, {
					modifiedFromBackoffice: true,
					textFieldsData: editableData,
				}));
			}
		}

		if (editableMedia) {
			dispatch(setEditableMediaData(page, editableMedia));
		}

		if (editableLinks) {
			dispatch(setEditableLinksData(page, editableLinks));
		}
	}
};

const getPageHtml = (
	page: number,
	resourceId: number,
	editorType: EditorType = EditorType.INDESIGN,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const {
		editor: {
			paths: { pagePath },
			previewPages,
		},
	} = getState();

	const isPreviewPage = previewPages.includes(page);
	if (isPreviewPage) {
		return;
	}

	try {
		const modifiedPagePath = editorType === EditorType.HTML
			? pagePath
			: getPageHtmlPath(pagePath, page);
		const pageHtml = await ApiUtils.getEditorResource(resourceId, modifiedPagePath);

		if (!isEditorOpened()) {
			return;
		}

		if (editorType === EditorType.HTML) {
			dispatch(parseHtmlEditorPage(page, pageHtml));
		} else {
			dispatch(parseIndesignEditorPage(page, pageHtml, resourceId));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const getClientIdAndResourceId = (): { clientId: number, resourceId: number } => {
	const { routerParamsHistory } = store.getState();
	const { params: { clientId, resourceId } } = routerParamsHistory[routerParamsHistory.length - 1];
	return {
		clientId: +clientId,
		resourceId: +resourceId,
	};
};

// this check is used to prevent some actions in case user left Editor before it was loaded
const isEditorOpened = (state?: StoreState): boolean => {
	const { routerParamsHistory } = state || store.getState();
	const currentRoute = RoutesUtils.getCurrentRoute(routerParamsHistory);
	return currentRoute.includes(RoutePath.Editor);
};

const getHtmlEditorData = (clientId: number, resourceId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const data = await ApiUtils.getHtmlEditorData(clientId, resourceId);
		const pagesData: Api.EditorThumbnailData[] = [{
			label: data.label,
			position: 1,
			proposal: false,
			type: Api.AssetItemFileTypes.COMPONENT,
			fileType: Api.AssetItemFileFormats.FILE,
		}];

		if (!isEditorOpened()) {
			return;
		}

		dispatch(setData(data));
		dispatch(setThumbnails(pagesData));

		await dispatch(getPageHtml(1, resourceId, EditorType.HTML));

		if (isEditorOpened()) {
			dispatch(setIsEditorLoaded(true));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

const getIndesignEditorData = (resource: Api.DriveItem, clientId: number, resourceId: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const {
			proposalStats: { status = '', forbiddenUpdate = false } = {},
			parent,
		} = resource;

		if (status !== Api.ProposalStatus.DRAFT || forbiddenUpdate) {
			return RoutesUtils.redirectToParentDir(parent);
		}

		const initialPage = 1;
		const {
			enrichmentInfo,
			pagesData,
		} = await ApiUtils.getIndesignEditorData(clientId, resourceId);

		if (!isEditorOpened()) {
			return;
		}

		const { format, totalPages } = enrichmentInfo;
		const isPdfProposal = format === Api.PublicationFormat.PDF_EDITABLE;

		if (isPdfProposal) {
			enrichmentInfo.nonEpubPages = Array.from({ length: totalPages }, (v, i) => i + 1);
		}

		dispatch(setData(enrichmentInfo));
		dispatch(setThumbnails(pagesData));

		if (!isPdfProposal) {
			const { cssPath, configsPath, jsPath } = enrichmentInfo;
			await dispatch(getResourcesData(resourceId, cssPath, configsPath, jsPath));
			await dispatch(getPageHtml(initialPage, resourceId));
		}

		if (isEditorOpened()) {
			dispatch(setIsEditorLoaded(true));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const getData = (clientId: number, resourceId: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { driveList: { driveItems } } = getState();

		const resource = driveItems[resourceId] || await ApiUtils.getMeta(resourceId, true, true);
		dispatch(updateDriveItem(resource));

		const isEditableComponent = resource.type === Api.DriveItemType.Component && resource.componentType === Api.ComponentType.ZIP_EDITABLE;
		const editorType = isEditableComponent
			? EditorType.HTML
			: EditorType.INDESIGN;

		dispatch(setEditorType(editorType));

		if (editorType === EditorType.HTML) {
			await dispatch(getHtmlEditorData(clientId, resourceId));
		} else {
			await dispatch(getIndesignEditorData(resource, clientId, resourceId));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const changePage = (page: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const action: ChangePageAction = {
		type: EditorActionKeys.CHANGE_PAGE,
		page,
	};
	dispatch(action);

	const {
		editor: {
			pages,
			slider: { prevPage },
		},
	} = getState();

	const { resourceId } = getClientIdAndResourceId();
	const { width, height } = pages[prevPage];
	dispatch(zoomPage(ZoomType.FIT));
	dispatch(addPage(page, width, height));
	dispatch(getPageHtml(page, resourceId));
};

export const savePage = (page: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			editor: {
				type,
				pages: { [page]: { defaultHtml, html } },
			},
		} = getState();

		const { clientId, resourceId } = getClientIdAndResourceId();
		const editableData = {
			editableTextFields: selectors.getPageEditableTextFields(getState(), page),
			editableMedia: selectors.getPageEditableMedia(getState(), page),
			editableLinks: selectors.getPageEditableLinks(getState(), page),
		};

		const htmlToSave = type === EditorType.HTML
			? htmlEditorUtils.getPageHtmlToSave(defaultHtml, editableData)
			: indesignEditorUtils.getPageHtmlToSave(defaultHtml, editableData);

		if (type === EditorType.HTML) {
			await ApiUtils.saveHtmlEditorPage(clientId, resourceId, htmlToSave);
		} else {
			await ApiUtils.saveIndesignEditorPage(clientId, resourceId, page, htmlToSave);
		}

		dispatch(setPageData({
			page,
			defaultHtml: htmlToSave,
			html,
		}));
	} catch (error) {
		dispatch(createNotification('error.save_editor_data_error'));
	}
};

export const createCombo = (resource: Api.DriveItem) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: EditorActionKeys.CREATE_COMBO,
		});

		const {
			editor: {
				label: { label },
			},
		} = getState();

		const { clientId, resourceId } = getClientIdAndResourceId();
		const resourcesGIds = [resourceId, resource.id];
		const { status } = await ApiUtils.createCombo(clientId, resourceId, resourcesGIds, label);

		const onProgressUpdate = (percentage: number) => {
			dispatch({
				type: EditorActionKeys.UPDATE_COMBO_PERCENTAGE,
				percentage,
			});
		};

		const onProgressError = () => {
			dispatch(finishCombo());
			dispatch(createNotification('error.combine_error'));
		};

		const onProgressFinish = async () => {
			const {
				editor: {
					slider: { totalPages },
				},
			} = getState();
			dispatch(reset());
			await dispatch(getData(clientId, resourceId));

			if (isEditorOpened()) {
				dispatch(changePage(totalPages + 1));
			}
		};

		const shouldCancelProgress = (state: StoreState) => {
			return !isEditorOpened(state);
		};

		const onProgressCancel = () => {
			dispatch(reset());
		};

		if (![
			Api.AssetItemStatus.LIVE,
			Api.AssetItemStatus.ERROR,
		].includes(status)) {
			await ApiUtils.getPublicationProgress(clientId, resourceId, onProgressUpdate, onProgressError, onProgressFinish, shouldCancelProgress, onProgressCancel);
		} else {
			if (status === Api.AssetItemStatus.LIVE) {
				onProgressFinish();
			}

			if (status === Api.AssetItemStatus.ERROR) {
				onProgressError();
			}
		}
	} catch (error) {
		dispatch(finishCombo());
		dispatch(createNotification('error.combine_error'));
	}
};

export const sendRenameRequest = (label: string) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { clientId, resourceId } = getClientIdAndResourceId();
		const { newLabel } = await ApiUtils.renameResource(clientId, resourceId, label);
		dispatch(rename(newLabel));
		dispatch(updateDriveItemName(resourceId, newLabel));
	} catch (error) {
		dispatch(setRenameError(true));

		if (ApiUtils.isDuplicateFileNameError(error.responseText)) {
			dispatch(createNotification('error.duplicate_file_name'));
		} else {
			dispatch(createNotification('error.rename_asset_error'));
		}
	}
};

const finishPagesUpdate = (newPages: number[], isPageSelectionActive?: boolean) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		// is used for adding as hash to thumbnail urls to make a browser to reload images
		dispatch(updateVersion());
		dispatch(reset());
		const { clientId, resourceId } = getClientIdAndResourceId();
		await dispatch(getData(clientId, resourceId));

		if (isEditorOpened()) {
			const {
				editor: {
					slider: { totalPages },
				},
			} = getState();

			if (isPageSelectionActive && totalPages > 1) {
				dispatch(restorePageSelection(newPages));
			}

			dispatch(changePage(newPages[0]));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};

export const removePages = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				slider: { page, totalPages },
				pageSelection: { pages: selectedPages, isActive },
			},
		} = getState();

		const { clientId, resourceId } = getClientIdAndResourceId();
		const pagesToRemove = isActive ? selectedPages : [page];
		const firstPageToRemove = isActive ? selectedPages[0] : page;
		const pageTogo = firstPageToRemove > (totalPages - pagesToRemove.length)
			? firstPageToRemove - 1
			: firstPageToRemove;

		await ApiUtils.removePublicationPages(clientId, resourceId, pagesToRemove);
		await dispatch(finishPagesUpdate([pageTogo], isActive));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.remove_page_error'));
	}
};

export const movePages = (pages: number[], newPosition: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				pageSelection,
			},
		} = getState();

		const { clientId, resourceId } = getClientIdAndResourceId();
		const isMoveToTop = pages[0] > newPosition;
		const newPages = pages.map((page, index) => (
			isMoveToTop
				? newPosition + index
				: newPosition + index - (pages.length - 1)
		));

		await ApiUtils.movePublicationPages(clientId, resourceId, pages, newPosition);
		await dispatch(finishPagesUpdate(newPages, pageSelection.isActive));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.move_page_error'));
	}
};

export const duplicatePage = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				slider: { page },
				pageSelection: { pages: selectedPages, isActive },
			},
		} = getState();

		const { clientId, resourceId } = getClientIdAndResourceId();
		const pageToDuplicate = isActive && selectedPages.length === 1 ? selectedPages[0] : page;

		await ApiUtils.duplicatePublicationPage(clientId, resourceId, pageToDuplicate);
		await dispatch(finishPagesUpdate([pageToDuplicate + 1]));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.duplicate_page_error'));
	}
};

const saveConfigs = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { clientId, resourceId } = getClientIdAndResourceId();
		const { editor: { configs } } = getState();

		await ApiUtils.saveIndesignEditorConfigs(clientId, resourceId, JSON.stringify(configs));
	} catch (error) {
		dispatch(createNotification('error.save_editor_data_error'));
	}
};

const saveEditableData = (page: number, data: EditableData) => (dispatch: Dispatch<StoreState>) => {
	const action: SetEditableDataAction = {
		type: EditorActionKeys.SET_EDITABLE_DATA,
		page,
		data,
	};
	dispatch(action);
	dispatch(saveConfigs());
};

export const setPageBackground = (page: number, data: BackgroundMediaData | null, isGeneralBgData: boolean) => (dispatch: Dispatch<StoreState>) => {
	const action: SetPageBackgroundAction = {
		type: EditorActionKeys.SET_PAGE_BACKGROUND,
		page,
		data,
		isGeneralBgData,
	};
	dispatch(action);
	dispatch(saveConfigs());
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions.ts